import { __rest } from 'tslib';

/**
 * Traverse the form configuration extracting the trigger configurations for each field.
 * Eventually a Map object is created mapping the component field id with the list of functions to
 * trigger.
 *
 * @param formConfig the original form config
 * @returns A Map containing a map between fieldid and functions to be triggered
 */
var getTriggersMap = function (formConfig) {
    var triggerMap = new Map();
    deepFlattenConfig(formConfig)
        .filter(function (component) { return component.triggers; })
        .forEach(function (component) {
        var triggers = component.triggers;
        Object.entries(triggers || {}).forEach(function (_a) {
            var _b;
            var key = _a[0], fx = _a[1];
            if (triggerMap.has(key)) {
                (_b = triggerMap.get(key)) === null || _b === void 0 ? void 0 : _b.push({ field: component.id, fx: fx });
            }
            else {
                triggerMap.set(key, [{ field: component.id, fx: fx }]);
            }
        });
    });
    return triggerMap;
};
/**
 * Traverses the whole form configuration returning a flat version.
 * It returns an array of ICertainComponent with no children reference.
 *
 * @param formConfig the original form config
 * @returns flatterned version of the config
 */
var deepFlattenConfig = function (formConfig) {
    if (formConfig === void 0) { formConfig = []; }
    var flattenConfig = [];
    formConfig.forEach(function (formConfigStep) {
        flattenConfig = flattenConfig.concat(extractConfig(formConfigStep.components));
    });
    return flattenConfig;
};
/**
 * Recursive function that traverse the config extracting and processing
 * children
 * @param components the list of components' config to process
 * @returns the flatten version of components' config
 */
var extractConfig = function (components) {
    var configItems = [];
    components.forEach(function (component) {
        if (component) {
            var children = component.children, rest = __rest(component, ["children"]);
            var childrenItems = [];
            if (children) {
                childrenItems = extractConfig(children);
            }
            configItems = configItems.concat(rest, childrenItems);
        }
    });
    return configItems;
};
/**
 * Given a list of key-value pairs, representing the initial value for each field,
 * this function returns the initial form state as the form builder expects it
 * @param values an array of field-value pairs
 * @returns initial form state
 */
function getInitialFormState(values) {
    var result = {};
    values.forEach(function (_a) {
        var field = _a[0], value = _a[1];
        result[field] = getFieldState(field, value);
    });
    return result;
}
function getFieldState(id, value) {
    return {
        id: id,
        value: value,
        touched: true,
        isValid: true,
        isValidationPending: false,
    };
}
function getComponentInFormElement(field, component) {
    if (field.component === component) {
        return field;
    }
    if (field.children) {
        return field.children.find(function (child) {
            return getComponentInFormElement(child, component);
        });
    }
}
function findComponentIdByServerId(components, errorKey) {
    for (var _i = 0, components_1 = components; _i < components_1.length; _i++) {
        var component = components_1[_i];
        if (component.serverId === errorKey) {
            return component.id;
        }
        if (component.children) {
            var children = Array.isArray(component.children)
                ? component.children
                : [component.children];
            var childId = findComponentIdByServerId(children, errorKey);
            if (childId) {
                return childId;
            }
        }
    }
    return null;
}
/**
 * Given a form state object containing form components,
 * this function extracts and returns only the raw values of each field.
 * @param form a form state object containing fieldsdata
 * @returns a plain object with only field values
 */
function extractValuesOnly(form) {
    var valuesOnly = {};
    Object.entries(form).forEach(function (_a) {
        var key = _a[0], field = _a[1];
        valuesOnly[key] = field.value;
    });
    return valuesOnly;
}

export { deepFlattenConfig, extractConfig, extractValuesOnly, findComponentIdByServerId, getComponentInFormElement, getInitialFormState, getTriggersMap };
